import api from './api';

// **INTERFACES**
export interface Question {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'checkbox' | 'radio' | 'file' | 'date' | 'signature' | 'section' | 'yesno' | 'email' | 'number' | 'info_text' | 'admin_file';
  
  // **TITEL/LABEL PROPERTIES - ERWEITERT**
  label?: {
    de?: string;
    en?: string;
  } | string;
  
  title?: {
    de?: string;
    en?: string;
  } | string;
  
  description?: {
    de?: string;
    en?: string;
  } | string;
  
  placeholder?: {
    de?: string;
    en?: string;
  } | string;
  
  required?: boolean;
  order?: number;
  
  validation?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    fileTypes?: string[];
    maxFileSize?: number;
  };
  
  options?: Array<{
    value: string;
    label: {
      de?: string;
      en?: string;
    } | string;
  }>;
  
  conditional?: {
    dependsOn: string;
    showWhen: string | string[];
  };
  
  // **NEU: Info-Text spezifische Properties**
  infoText?: {
    de: string;
    en: string;
  };
  
  // **NEU: Admin-File spezifische Properties**
  adminFiles?: Array<{
    originalName: string;
    fileName: string;
    filePath: string;
    size: number;
    mimeType: string;
    uploadedAt: string;
  }>;
}

// **ERWEITERTE ANSWER INTERFACE**
export interface Answer {
  questionId: string;
  answer: any;
  attachments?: string[];
  answeredAt: string;
}

// **NEU: QUESTIONNAIRE ITEM INTERFACE FÜR SIDEBAR**
export interface QuestionnaireItem {
  id: string;
  title: string;
  type: string;
  status: 'completed' | 'in_progress' | 'pending' | 'overdue';
  deadline: string;
  progress: number;
  accessToken: string;
  supplierId: string;
  sentAt?: string;
  completedAt?: string;
}

export interface SupplierInfo {
  _id?: string; // **HINZUGEFÜGT FÜR BACKEND COMPATIBILITY**
  name: string;
  email: string;
  contactPerson?: string;
  kreditorNumber?: string; // **HINZUGEFÜGT FÜR KREDITOR-NUMMER**
  gln?: string; // **HINZUGEFÜGT FÜR GLN**
  mustChangePassword?: boolean; // **HINZUGEFÜGT FÜR PASSWORT-MANAGEMENT**
  isFirstLogin?: boolean; // **HINZUGEFÜGT FÜR ERSTE ANMELDUNG**
}

// **SUPPLIER QUESTIONNAIRE DATA**
export interface SupplierQuestionnaireData {
  submission: {
    id: string;
    accessToken: string;
    status: string;
    deadline: string;
    progressPercentage: number;
    answeredQuestions: number;
    totalQuestions: number;
    language: string;
    isLoginRequired: boolean;
    progress?: number;
    completedAt?: string;
  };
  supplier: SupplierInfo;
  template: {
    title: { de: string; en?: string };
    description: { de: string; en?: string };
    type: string;
    version: string;
    questions: Question[];
    // **NEU: Artikel-Daten**
    selectedArticles?: string[]; // Array von Artikel-IDs
    articles?: Array<{
      _id: string;
      articleNumber: string;
      name: string;
      description?: string;
      supplierId: string;
      supplierName?: string;
    }>;
  };
  answers: Answer[];
  canEdit: boolean;
}

// **API FUNCTIONS**
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const loadQuestionnaire = async (accessToken: string): Promise<SupplierQuestionnaireData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/questionnaire/${accessToken}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Laden des Fragebogens');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Load questionnaire error:', error);
    throw error;
  }
};

export const loginSupplier = async (
  accessToken: string, 
  username: string, 
  password: string
): Promise<{ sessionToken: string; supplier: any; submission: any }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/questionnaire/${accessToken}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Anmeldung fehlgeschlagen');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const saveAnswer = async (
  accessToken: string,
  sessionToken: string,
  questionId: string,
  answer: any,
  attachments?: File[]
): Promise<{ progress: any; attachments?: string[] }> => {
  try {
    const formData = new FormData();
    formData.append('questionId', questionId);
    formData.append('answer', JSON.stringify(answer));
    formData.append('sessionToken', sessionToken);
    
    if (attachments) {
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    
    const response = await fetch(`${API_BASE_URL}/public/questionnaire/${accessToken}/answer`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Speichern der Antwort');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Save answer error:', error);
    throw error;
  }
};

export const submitQuestionnaire = async (
  accessToken: string,
  sessionToken: string,
  signature?: string,
  submitterName?: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/questionnaire/${accessToken}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionToken,
        signature,
        submitterName,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Abschließen des Fragebogens');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Submit questionnaire error:', error);
    throw error;
  }
};

// **NEU: ALLE FRAGEBÖGEN EINES LIEFERANTEN LADEN**
export const loadSupplierQuestionnaires = async (supplierEmail: string): Promise<QuestionnaireItem[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/supplier/${encodeURIComponent(supplierEmail)}/questionnaires`);
    
    if (!response.ok) {
      throw new Error('Fehler beim Laden der Lieferanten-Fragebögen');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Load supplier questionnaires error:', error);
    throw error;
  }
};

// **PORTAL API FUNCTIONS**

// **1. PORTAL LOGIN WITH PASSWORD**
export const loginPortalWithPassword = async (
  masterToken: string,
  username: string,
  password: string
): Promise<{ 
  sessionToken: string; 
  supplier: SupplierInfo; 
  mustChangePassword: boolean;
  isFirstLogin: boolean;
  message: string;
}> => {
  try {
    console.log('🔐 Portal login with password for masterToken:', masterToken);
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/auth`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        password
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Login fehlgeschlagen');
    }

    const data = await response.json();
    console.log('✅ Portal login successful:', data);
    
    // **STORE SESSION TOKEN CONSISTENTLY**
    if (data.sessionToken) {
      sessionStorage.setItem('supplierPortalSession', data.sessionToken);
      sessionStorage.setItem('supplierPortalSessionTime', new Date().toISOString());
      console.log('💾 Session token stored:', data.sessionToken.substring(0, 20) + '...');
      
      // Dispatch custom event to notify GlobalDataContext of session update
      window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
      console.log('📡 Supplier session update event dispatched');
    }
    
    return {
      sessionToken: data.sessionToken,
      supplier: data.supplier,
      mustChangePassword: data.mustChangePassword || false,
      isFirstLogin: data.isFirstLogin || false,
      message: data.message || 'Login erfolgreich'
    };
  } catch (error: any) {
    console.error('❌ Portal login failed:', error);
    throw new Error(error.message || 'Login fehlgeschlagen');
  }
};

// **2. PORTAL DASHBOARD LADEN - MIT SUPPLIER CONTEXT SUPPORT & RATE LIMIT HANDLING**
export const loadPortalDashboard = async (
  masterToken: string,
  sessionToken: string
): Promise<{ supplier: SupplierInfo; questionnaires: QuestionnaireItem[]; articles?: any[] }> => {
  try {
    console.log('📡 Loading portal dashboard with consistent session token:', sessionToken.substring(0, 20) + '...');
    
    // **CHECK FOR ACTIVE SUPPLIER CONTEXT (für Shared Portal)**
    const activeSupplierContext = sessionStorage.getItem('activeSupplierContext');
    let contextSupplierId = null;
    
    if (activeSupplierContext) {
      try {
        const context = JSON.parse(activeSupplierContext);
        contextSupplierId = context.supplierId;
        console.log('🔄 Using active supplier context:', context.name, contextSupplierId);
      } catch (err) {
        console.log('⚠️ Invalid supplier context, clearing...');
        sessionStorage.removeItem('activeSupplierContext');
      }
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    };
    
    // **ADD CONTEXT HEADER if switching supplier**
    if (contextSupplierId) {
      headers['X-Active-Supplier-Context'] = contextSupplierId;
    }
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}`, {
      headers
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      
      // **SPECIAL HANDLING FOR AUTHENTICATION REQUIRED**
      if (response.status === 401 && errorData.requiresLogin) {
        console.log('🔐 Authentication required, should redirect to login');
        throw new Error('REDIRECT_TO_LOGIN');
      }
      
      // **SPECIAL HANDLING FOR RATE LIMITING**
      if (response.status === 429) {
        const retryAfter = errorData.retryAfter || 5;
        console.warn(`⏰ Rate limited! Retry after ${retryAfter} seconds`);
        throw new Error(`Zu viele Anfragen. Bitte warten Sie ${retryAfter} Sekunden und versuchen Sie es erneut.`);
      }
      
      console.error('❌ Portal dashboard error:', errorData);
      throw new Error(errorData.message || 'Fehler beim Laden des Portal-Dashboards');
    }
    
    const data = await response.json();
    console.log('✅ Portal dashboard loaded successfully');
    
    return {
      supplier: data.portal?.supplier || data.supplier, // Support both formats
      questionnaires: data.questionnaires,
      articles: data.articles || []
    };
  } catch (error) {
    console.error('❌ Portal dashboard error:', error);
    throw error;
  }
};

// **3. PORTAL QUESTIONNAIRE LADEN**
export const loadPortalQuestionnaire = async (
  masterToken: string,
  accessToken: string,
  sessionToken: string
): Promise<SupplierQuestionnaireData> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/questionnaire/${accessToken}?session=${sessionToken}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Laden des Portal-Fragebogens');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Portal questionnaire error:', error);
    throw error;
  }
};

// **4. PORTAL ANSWER SPEICHERN**
export const savePortalAnswer = async (
  masterToken: string,
  accessToken: string,
  sessionToken: string,
  questionId: string,
  answer: any,
  attachments?: File[],
  language?: 'de' | 'en'
): Promise<{ progress: any; attachments?: string[] }> => {
  try {
    const formData = new FormData();
    formData.append('questionId', questionId);
    formData.append('answer', JSON.stringify(answer));
    formData.append('sessionToken', sessionToken);
    if (language) {
      formData.append('language', language);
    }
    
    if (attachments) {
      attachments.forEach(file => {
        formData.append('attachments', file);
      });
    }
    
    // **USE PORTAL-SPECIFIC ROUTE**
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/questionnaire/${accessToken}/answer`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Speichern der Antwort');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Portal save answer error:', error);
    throw error;
  }
};

// **5. PORTAL QUESTIONNAIRE SUBMIT**
export const submitPortalQuestionnaire = async (
  masterToken: string,
  accessToken: string,
  sessionToken: string,
  signature?: string,
  submitterName?: string
): Promise<any> => {
  try {
    // **USE EXISTING PUBLIC ROUTE WITH SESSION TOKEN**
    const response = await fetch(`${API_BASE_URL}/public/questionnaire/${accessToken}/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionToken,
        signature,
        submitterName,
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Abschließen des Fragebogens');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Portal submit questionnaire error:', error);
    throw error;
  }
};

// **PASSWORD MANAGEMENT FUNCTIONS**

// **CHANGE PASSWORD**
export const changePassword = async (
  masterToken: string,
  sessionToken: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionToken, currentPassword, newPassword }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Ändern des Passworts');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Change password error:', error);
    throw error;
  }
};

// **REQUEST PASSWORD RESET**
export const requestPasswordReset = async (
  email: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/password-reset/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler bei der Passwort-Anfrage');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Request password reset error:', error);
    throw error;
  }
};

// **COMPLETE PASSWORD RESET**
export const completePasswordReset = async (
  resetToken: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/password-reset/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ resetToken, newPassword }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler beim Zurücksetzen des Passworts');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Complete password reset error:', error);
    throw error;
  }
};

// **VALIDATE PASSWORD**
export const validatePassword = async (
  password: string
): Promise<{ 
  success: boolean; 
  validation: { 
    isValid: boolean; 
    errors: string[]; 
    strength: 'weak' | 'medium' | 'strong' 
  } 
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/public/validate-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Fehler bei der Passwort-Validierung');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Validate password error:', error);
    throw error;
  }
};

// **ADMIN: SEND PASSWORD RESET TO SUPPLIER**
export const sendPasswordReset = async (
  supplierId: string
): Promise<{ success: boolean; message: string }> => {
  try {
    console.log('🔐 Sending password reset for supplier:', supplierId);
    
    // **USE API INSTANCE WITH MSAL TOKEN INTERCEPTOR**
    const response = await api.post(`/suppliers/${supplierId}/password-reset`);
    
    return response.data;
  } catch (error: any) {
    console.error('Send password reset error:', error);
    
    // Extract meaningful error message
    const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Senden des Passwort-Resets';
    throw new Error(errorMessage);
  }
};

// **LOAD SUPPLIER ARTICLES WITH CONTEXT SUPPORT**
export const loadSupplierArticles = async (masterToken: string, sessionToken: string) => {
  try {
    console.log('🔍 Loading supplier articles...');
    
    // **GET SUPPLIER CONTEXT from sessionStorage**
    let contextSupplierId: string | null = null;
    const activeSupplierContext = sessionStorage.getItem('activeSupplierContext');
    
    if (activeSupplierContext) {
      try {
        const context = JSON.parse(activeSupplierContext);
        contextSupplierId = context.supplierId;
        console.log('🔄 Using active supplier context for articles:', context.name, contextSupplierId);
      } catch (err) {
        console.log('⚠️ Invalid supplier context, clearing...');
        sessionStorage.removeItem('activeSupplierContext');
      }
    }
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${sessionToken}`,
      'Content-Type': 'application/json'
    };
    
    // **ADD CONTEXT HEADER if switching supplier**
    if (contextSupplierId) {
      headers['X-Active-Supplier-Context'] = contextSupplierId;
    }
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/articles`, {
      headers
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('❌ Articles loading error:', errorData);
      throw new Error(errorData.message || 'Fehler beim Laden der Artikel');
    }
    
    const data = await response.json();
    console.log('✅ Articles loaded successfully:', data.articles?.length || 0);
    return data;
    
  } catch (error) {
    console.error('Load articles error:', error);
    throw error;
  }
};

// **GET SINGLE SUPPLIER ARTICLE BY ID**
export const getSupplierArticleById = async (masterToken: string, sessionToken: string, articleId: string) => {
  try {
    console.log('🔍 Loading single supplier article:', articleId);
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/articles/${articleId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ Single article loading error:', errorData);
      throw new Error(`Failed to load article: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Single article loaded successfully');
    return data.article; // Extract the article from the response
  } catch (error) {
    console.error('Load single article error:', error);
    throw error;
  }
};

// **CREATE PORTAL FOR SUPPLIER**
export const createPortalForSupplier = async (supplierId: string) => {
  try {
    const response = await api.post(`/suppliers/${supplierId}/create-portal`);

    return response.data;
  } catch (error: any) {
    console.error('Create portal error:', error);
    
    // Extract meaningful error message
    const errorMessage = error.response?.data?.error || error.message || 'Fehler beim Erstellen des Portals';
    throw new Error(errorMessage);
  }
};

// **UPDATE SUPPLIER ARTICLE (Session-based Authentication)**
export const updateSupplierArticle = async (masterToken: string, sessionToken: string, articleId: string, updateData: any) => {
  try {
    console.log('🔄 Updating supplier article via public API:', articleId);
    
    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? 'https://your-production-domain.com/api' 
      : 'http://localhost:5000/api';
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/articles/${articleId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ Article update error:', errorData);
      throw new Error(`Failed to update article: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Supplier article updated successfully');
    return data.article; // Extract the article from the response
  } catch (error) {
    console.error('Update supplier article error:', error);
    throw error;
  }
};

// **CREATE SUPPLIER ARTICLE (Session-based Authentication)**
export const createSupplierArticle = async (masterToken: string, sessionToken: string, articleData: any) => {
  try {
    console.log('🔄 Creating new supplier article via public API');
    
    const API_BASE_URL = process.env.NODE_ENV === 'production' 
      ? 'https://your-production-domain.com/api' 
      : 'http://localhost:5000/api';
    
    const response = await fetch(`${API_BASE_URL}/public/portal/${masterToken}/articles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`
      },
      body: JSON.stringify(articleData)
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('❌ Article creation error:', errorData);
      throw new Error(`Failed to create article: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Supplier article created successfully');
    return data.article; // Extract the article from the response
  } catch (error) {
    console.error('Create supplier article error:', error);
    throw error;
  }
};

// **SUPPLIER IMPORT FUNCTIONS**

// Import suppliers from Excel/CSV data (Enhanced with dual format support)
export const importSuppliersFromExcel = async (data: { suppliers: any[], headers?: string[] }): Promise<{
  success: boolean;
  message: string;
  successful: number;
  failed: number;
  updated: number;
  format: 'simple' | 'perfion';
  errors: Array<{
    row: number;
    supplierCode: string;
    error: string;
  }>;
  warnings: Array<{
    row: number;
    supplierCode: string;
    warning: string;
  }>;
}> => {
  try {
    const response = await api.post('/suppliers/import-excel', data);
    return response.data;
  } catch (error: any) {
    console.error('Supplier import error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Lieferanten-Import';
    throw new Error(errorMessage);
  }
};

// Validate supplier import data (Enhanced with dual format support)
export const validateSupplierExcelData = async (data: { suppliers: any[], headers?: string[] }): Promise<{
  success: boolean;
  message: string;
  successful: number;
  failed: number;
  updated: number;
  format: 'simple' | 'perfion';
  errors: Array<{
    row: number;
    supplierCode: string;
    error: string;
  }>;
  warnings: Array<{
    row: number;
    supplierCode: string;
    warning: string;
  }>;
}> => {
  try {
    const response = await api.post('/suppliers/validate-excel', data);
    return response.data;
  } catch (error: any) {
    console.error('Supplier validation error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Fehler bei der Lieferanten-Validierung';
    throw new Error(errorMessage);
  }
};

// **PERFION API SYNC FOR SUPPLIERS**

export interface SupplierPerfionSyncResult {
  success: boolean;
  message: string;
  apiSuppliers: number;
  processed: number;
  created: number;
  updated: number;
  errors: number;
  totalInDB: number;
  errorDetails?: string[];
}

/**
 * Sync suppliers from Perfion API
 */
export const syncSuppliersFromPerfionAPI = async (): Promise<SupplierPerfionSyncResult> => {
  try {
    console.log('🔄 Starting Perfion Supplier Sync...');
    
    const response = await api.post('/suppliers/sync-perfion');
    
    console.log('✅ Perfion Supplier Sync completed:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Perfion Supplier Sync error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Fehler bei der Perfion Synchronisation';
    throw new Error(errorMessage);
  }
};

/**
 * Test Perfion API connection for suppliers
 */
export const testSupplierPerfionConnection = async (): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> => {
  try {
    console.log('🔍 Testing Perfion Supplier API connection...');
    
    const response = await api.post('/suppliers/test-perfion');
    
    console.log('✅ Perfion Supplier API test result:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Perfion Supplier API test error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Fehler beim Perfion-Verbindungstest';
    throw new Error(errorMessage);
  }
};

/**
 * Search suppliers in Perfion API
 */
export const searchSuppliersPerfion = async (searchTerm: string): Promise<{
  success: boolean;
  suppliers: any[];
  count: number;
}> => {
  try {
    console.log('🔍 Searching suppliers in Perfion API:', searchTerm);
    
    const response = await api.post('/suppliers/search-perfion', { searchTerm });
    
    console.log('✅ Perfion Supplier search results:', response.data.count);
    return response.data;
  } catch (error: any) {
    console.error('❌ Perfion Supplier search error:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Fehler bei der Lieferantensuche';
    throw new Error(errorMessage);
  }
};